export type ClientShellCompany = {
  id: number;
  name: string;
  slug: string;
  status: 'active' | 'onboarding' | 'inactive';
  branding?: {
    logo_url: string | null;
    logo_mark_url: string | null;
  };
};

export type CaresShellTenantBranding = {
  company_id: number;
  company_name: string;
  company_slug: string;
  logo_url: string | null;
  logo_mark_url: string | null;
};

export type CaresShellGroupScope = {
  id: number;
  name: string;
  slug: string;
  kind: string | null;
  status: 'active' | 'inactive' | 'archived';
  role_keys: string[];
  company: ClientShellCompany | null;
};

export type CaresShellCompanyScope = ClientShellCompany & {
  membership_status: 'invited' | 'active' | 'suspended';
  role_keys: string[];
  groups: CaresShellGroupScope[];
};

export type CaresShellNavigation = {
  mySchedule: boolean;
  schedulerReview: boolean;
  scheduleSettings: boolean;
  adminSettings: boolean;
  faq: boolean;
  admin: boolean;
  companyProvisioning: boolean;
};

export type CaresShellBootstrap = {
  authenticated: boolean;
  authPage: {
    email: string | null;
    first_name: string | null;
    last_name: string | null;
    remember: boolean;
  };
  user: {
    name: string;
    email: string;
    status: string;
    avatar_url?: string | null;
  } | null;
  authProviders: {
    google: {
      configured: boolean;
    };
  };
  canAccessAdmin: boolean;
  navigation: CaresShellNavigation;
  clientShellCompanies: ClientShellCompany[];
  tenantCompanies: CaresShellCompanyScope[];
  activeTenantBranding: CaresShellTenantBranding | null;
  themeDefaults?: Record<string, string>;
  validationErrors?: Record<string, string[]>;
  status?: string | null;
};

const defaultBootstrap: CaresShellBootstrap = {
  authenticated: false,
  authPage: {
    email: null,
    first_name: null,
    last_name: null,
    remember: true,
  },
  user: null,
  authProviders: {
    google: {
      configured: false,
    },
  },
  canAccessAdmin: false,
  navigation: {
    mySchedule: false,
    schedulerReview: false,
    scheduleSettings: false,
    adminSettings: false,
    faq: false,
    admin: false,
    companyProvisioning: false,
  },
  clientShellCompanies: [],
  tenantCompanies: [],
  activeTenantBranding: null,
  themeDefaults: {},
  validationErrors: {},
  status: null,
};

declare global {
  interface Window {
    __CARES_BOOTSTRAP__?: CaresShellBootstrap;
  }
}

export const getCaresShellBootstrap = (): CaresShellBootstrap =>
  window.__CARES_BOOTSTRAP__ ?? defaultBootstrap;

const sortedCompanies = (companies: ClientShellCompany[]) =>
  [...companies].sort((left, right) => left.name.localeCompare(right.name));

export const setCaresShellBootstrap = (nextBootstrap: CaresShellBootstrap) => {
  window.__CARES_BOOTSTRAP__ = nextBootstrap;
  window.dispatchEvent(new CustomEvent('cares:bootstrap-updated'));
};

export const setClientShellCompanies = (companies: ClientShellCompany[]) => {
  setCaresShellBootstrap({
    ...getCaresShellBootstrap(),
    clientShellCompanies: sortedCompanies(companies),
  });
};

export const upsertClientShellCompany = (company: ClientShellCompany) => {
  const bootstrap = getCaresShellBootstrap();
  const nextCompanies = bootstrap.clientShellCompanies.filter((entry) => entry.id !== company.id);
  nextCompanies.push(company);

  setClientShellCompanies(nextCompanies);
};

export const removeClientShellCompany = (companyId: number) => {
  const bootstrap = getCaresShellBootstrap();

  setClientShellCompanies(
    bootstrap.clientShellCompanies.filter((company) => company.id !== companyId)
  );
};
